import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { FormControl, FormHelperText } from '../shared/FormControl';
import { Eye, EyeClosed } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  error?: boolean;
  variant?: 'outlined' | 'filled' | 'standard' | 'ghost';
  hideLabel?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  classNames?: { input?: string; label?: string; startAdornment?: string; endAdornment?: string };
  type?: 'text' | 'password' | 'email' | 'number' | 'search' | 'tel' | 'url' | 'date' | 'time' | 'datetime-local';
  dataTestId?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      hideLabel,
      helperText,
      error,
      variant = 'standard',
      startAdornment,
      endAdornment,
      required,
      disabled,
      id,
      placeholder,
      classNames,
      type = 'text',
      value,
      onChange,
      onBlur,
      onFocus,
      dataTestId,
      ...props
    },
    ref
  ) => {
    const { setFocus } = useFormContext() || {}; // Safely check if useFormContext is available
    const [showPassword, setShowPassword] = useState(false);
    const [focused, setFocused] = useState(false); // Local focus state for manual control if no React Hook Form

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (!setFocus) {
        // If React Hook Form's setFocus is not available, use local state
        setFocused(true);
      }
      onFocus?.(e); // Call onFocus from React Hook Form or custom logic
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (!setFocus) {
        // If React Hook Form's setFocus is not available, use local state
        setFocused(false);
      }
      onBlur?.(e); // Call onBlur from React Hook Form or custom logic

      if (setFocus && id) {
        setFocus(id); // Trigger React Hook Form's setFocus if it's available
      }
    };

    const togglePasswordVisibility = () => {
      setShowPassword((prev) => !prev);
    };

    const inputStyles = cn(
      'block w-full text-md transition-colors focus:outline-none bg-transparent',
      {
        'border rounded-md focus:ring-1 hover:ring-1 ring-neutral-100 dark:ring-neutral-700': variant === 'outlined',
        'border-b-2 focus:border-b-2 hover:border-b-neutral-100 dark:hover:border-b-neutral-700': variant === 'filled' || variant === 'standard',
        'border-gray-300 dark:border-gray-600 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-primary-500 dark:focus:ring-primary-400': !error,
        'border-critical focus:border-critical focus:ring-critical': error,
        'text-gray-500 dark:text-gray-400 cursor-not-allowed': disabled,
        'pl-12': startAdornment || type === 'search',
        'pr-12': endAdornment || type === 'password' || type === 'search',
      },
      variant === 'outlined' && 'px-1 py-3',
      variant === 'filled' && 'px-1 pt-6 pb-2 bg-gray-100 dark:bg-gray-800',
      (variant === 'standard' || variant === 'ghost') && 'px-0 pt-4 pb-2',
      'px-2',
      startAdornment && 'pl-12',
      endAdornment && 'pr-12',
      classNames?.input
    );

    const labelStyles = cn(
      'absolute left-2 transition-all pointer-events-none ',
      {
        'text-gray-500 dark:text-gray-400': !focused && !value,
        'text-xs text-gray-500 dark:text-gray-400': focused || value || startAdornment,
        'top-3': variant === 'outlined' && !focused && !value,
        'top-4': variant !== 'outlined' && !focused && !value,
        'top-[-8px] ': variant === 'outlined' && (focused || value || startAdornment),
        'top-[8px] text-gray-500 dark:text-gray-400': variant === 'filled' && (focused || value || startAdornment),
        'top-[-6px]': (variant === 'standard' || variant === 'ghost') && (focused || value || startAdornment),
        'text-xs bg-white dark:bg-[#52606d] px-1': variant === 'outlined' && (focused || value || startAdornment),
        'text-primary-500 dark:text-primary-400': focused && !error,
        'text-critical': error,
      },
      classNames?.label
    );

    return (
      <FormControl error={error}>
        <div className="relative m-2">
          {!hideLabel && (
            <label htmlFor={id} className={labelStyles}>
              {label}
              {required && <span className="text-critical ml-1">*</span>}
            </label>
          )}
          {startAdornment && (
            <div className={cn('absolute inset-y-0 left-0 flex items-center pl-4', classNames?.startAdornment)}>
              {startAdornment}
            </div>
          )}
          <input
            type={type === 'password' && showPassword ? 'text' : type}
            className={inputStyles}
            ref={ref}
            id={id}
            disabled={disabled}
            required={required}
            aria-invalid={error}
            aria-describedby={helperText ? `${id}-helper-text` : undefined}
            onFocus={handleFocus} // Call onFocus from React Hook Form or custom logic
            onBlur={handleBlur} // Handle blur and trigger setFocus if necessary
            placeholder={undefined}
            value={value}
            autoComplete={props.autoComplete || 'off'}
            onChange={onChange}
            data-testid={dataTestId}
            {...props}
          />
          {endAdornment || type === 'password' ? (
            <div
              className={cn(
                'absolute inset-y-0 right-0 flex items-center pr-4 cursor-pointer',
                classNames?.endAdornment
              )}
            >
              {type === 'password' ? (
                <span className="cursor-pointer" onClick={togglePasswordVisibility}>
                  {showPassword ? <Eye /> : <EyeClosed />}
                </span>
              ) : (
                endAdornment
              )}
            </div>
          ) : null}
        </div>
        {helperText && (
          <FormHelperText id={`${id}-helper-text`} className={`px-4 ${error && 'text-critical'}`} error={error}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  }
);

Input.displayName = 'Input';
